/**
 * TourFunnelTile — super-admin dashboard card rendering the per-step
 * drop-off curve for the Demo Toolkit onboarding tour. Powered by
 * `/api/analytics/tour/funnel`.
 *
 * Visualises as horizontal bars where width = % of the top-of-funnel
 * cohort that reached that step. The biggest step-to-step drop-off
 * is highlighted with a callout line so PMs can act: "step 3→4 lost
 * 52% — is the narration copy too dense?".
 *
 * Empty state (no events collected yet) renders a friendly prompt
 * rather than an error so the tile doesn't look broken on a fresh
 * tenant right after deploy.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Compass, TrendingDown, AlertTriangle } from 'lucide-react';
import { useTelemetryTile } from '../../hooks';


const WINDOW_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const STEP_LABELS = ['Session', 'Capture', 'Seed', 'Narration', 'Actions'];


function formatPct(x) {
  if (typeof x !== 'number' || Number.isNaN(x)) return '—';
  return `${Math.round(x * 100)}%`;
}

/** Find the largest `step N → step N+1` drop-off. Returns null if the
 *  funnel is too sparse (fewer than 2 steps with users). */
function findBiggestDropOff(perStep) {
  if (!perStep || perStep.length < 2) return null;
  let worst = null;
  for (let i = 0; i < perStep.length - 1; i += 1) {
    const here = perStep[i].users_reached;
    const next = perStep[i + 1].users_reached;
    if (!here) continue;
    const lost = here - next;
    const lostPct = lost / here;
    if (lostPct > 0 && (!worst || lostPct > worst.lostPct)) {
      worst = { from: i, to: i + 1, lostPct };
    }
  }
  return worst;
}


export default function TourFunnelTile() {
  const [windowDays, setWindowDays] = useState(30);
  const { data, loading, error } = useTelemetryTile({
    url: `/analytics/tour/funnel?tour_id=demo_toolkit_v1&window_days=${windowDays}`,
    deps: [windowDays],
  });

  if (error === 'forbidden') return null;

  const totals = data?.totals || {
    events: 0, unique_users: 0, finished: 0, skipped: 0,
    completion_rate: 0, skip_rate: 0,
  };
  const perStep = data?.per_step || [];
  const top = perStep[0]?.users_reached || 0;
  const worstDrop = findBiggestDropOff(perStep);

  return (
    <Card data-testid="tour-funnel-tile" className="border-amber-200 bg-gradient-to-br from-white to-amber-50/40">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Compass className="h-4 w-4 text-amber-500" />
              Onboarding Tour Funnel
            </CardTitle>
            <CardDescription className="mt-0.5">
              How far new super-admins get through the Demo Toolkit tour. Identifies the step that confuses people most — so you know which copy to tighten.
            </CardDescription>
          </div>
          <div className="inline-flex bg-white border border-slate-200 rounded-md p-0.5" data-testid="tour-funnel-window-selector">
            {WINDOW_OPTIONS.map((o) => (
              <button
                key={o.days}
                type="button"
                onClick={() => setWindowDays(o.days)}
                className={
                  'px-2 py-0.5 text-[11px] font-medium rounded transition-colors ' +
                  (windowDays === o.days
                    ? 'bg-amber-500 text-white'
                    : 'text-slate-600 hover:bg-slate-100')
                }
                data-testid={`tour-funnel-window-${o.days}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-slate-500" data-testid="tour-funnel-loading">Loading…</p>
        ) : error ? (
          <p className="text-xs text-rose-600" data-testid="tour-funnel-error">
            Couldn't load tour funnel.
          </p>
        ) : totals.unique_users === 0 ? (
          <div className="text-xs text-slate-500 leading-relaxed" data-testid="tour-funnel-empty">
            No tour activity in the last {windowDays} days yet. The funnel
            will populate once super-admins open the Demo Toolkit on Acme
            for the first time.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Unique users</p>
                <p className="text-lg font-bold text-amber-700 tabular-nums">{totals.unique_users}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Completion</p>
                <p className="text-lg font-bold text-emerald-700 tabular-nums">{formatPct(totals.completion_rate)}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Skip rate</p>
                <p className="text-lg font-bold text-rose-700 tabular-nums">{formatPct(totals.skip_rate)}</p>
              </div>
            </div>

            {worstDrop && (
              <div
                className="mb-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] leading-snug text-rose-800"
                data-testid="tour-funnel-drop-callout"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />
                <span>
                  Biggest drop-off: <strong>step {worstDrop.from + 1} → {worstDrop.to + 1}</strong>{' '}
                  ({STEP_LABELS[worstDrop.from] || `Step ${worstDrop.from + 1}`} →{' '}
                  {STEP_LABELS[worstDrop.to] || `Step ${worstDrop.to + 1}`})
                  {' — '}
                  <strong>-{formatPct(worstDrop.lostPct)}</strong> of the cohort bailed here.
                </span>
              </div>
            )}

            <ol className="space-y-1.5" data-testid="tour-funnel-list">
              {perStep.map((s, i) => {
                const pct = top ? s.users_reached / top : 0;
                const label = STEP_LABELS[i] || `Step ${i + 1}`;
                return (
                  <li
                    key={s.step}
                    className="flex items-center gap-2"
                    data-testid={`tour-funnel-row-${i}`}
                  >
                    <span className="w-20 text-[11px] text-slate-700">
                      <span className="mr-1 font-mono text-slate-400">{i + 1}.</span>
                      {label}
                    </span>
                    <div className="relative flex-1 h-4 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all"
                        style={{ width: `${Math.round(pct * 100)}%` }}
                      />
                    </div>
                    <span className="w-12 text-right text-[11px] font-semibold text-amber-700 tabular-nums">
                      {s.users_reached}
                    </span>
                    {s.users_skipped_at_step > 0 && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[11px] text-rose-600 tabular-nums"
                        title={`${s.users_skipped_at_step} user${s.users_skipped_at_step === 1 ? '' : 's'} skipped at this step`}
                      >
                        <TrendingDown className="h-3 w-3" />
                        {s.users_skipped_at_step}
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </CardContent>
    </Card>
  );
}
